import React, { useEffect, useState } from 'react';
import cookie from 'js-cookie';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { LOCAL_SHOW_SETUP_ACCOUNTS, LOCAL_DIRECT_PATH, REPORT_NAMES, SECTION_REPORT, REPORT_TYPE_TO_NAMES_MAP, STATUS_TAG_FIELD_REPORT, SCOPED, DATE_FORMATS, VIDEO_EVENTS_GROUP_BY_NAME } from '../constants';
import { subscriberNavKey } from '../containers/_nav/subscriber';
import { isRateField, sanitizeTableName } from './UtilReport';
import { INTENT_PROPERTIES_STORE } from '../constants/intent-rules';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';
import advancedFormat from 'dayjs/plugin/advancedFormat.js';
import { parse, isValid, format } from 'date-fns';
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

// Clear everything except items called "theme" and LOCAL_SHOW_SETUP_ACCOUNTS
export const clearLocal = () => {
     const theme = JSON.parse(localStorage.getItem('theme'));
     const localShowSetupAccounts = JSON.parse(localStorage.getItem(LOCAL_SHOW_SETUP_ACCOUNTS));
     const localShowInstallation = JSON.parse(localStorage.getItem('showInstallationScreen'));
     localStorage.clear();
     localStorage.setItem('theme', JSON.stringify(theme));
     localStorage.setItem(LOCAL_SHOW_SETUP_ACCOUNTS, JSON.stringify(localShowSetupAccounts));
     localStorage.setItem('showInstallationScreen', JSON.stringify(localShowInstallation));
     if (!window.location.href.includes('/login') && !window.location.href.includes('/logout') && !window.location.href.includes('/invite-user')) {
          const directPath = window.location.href.replace(window.location.origin, '');
          const splitDirect = directPath.split('/');

          localStorage.setItem(LOCAL_DIRECT_PATH, splitDirect.join('/'));
     }
};

export const getTokenCookie = () => {
     const token = cookie.get('token');

     if (token) {
          return token;
     }

     clearLocal();
     window.location.replace('/login');
};

export const removeTokenCookie = () => {
     let tokenOptions = { expires: 1 };

     if (window.location.hostname !== 'localhost') {
          tokenOptions.domain = 'listenlayer.com';
     }

     cookie.remove('token', tokenOptions);
};

export const isoDateToString = (isoDate) => {
     return new Date(isoDate).toLocaleDateString();
};

export const formatDate = (date, format) => {
     return dayjs(date).format(format);
};

export const formatDateUTC = (date, format) => {
     return dayjs.utc(date).format(format);
};

export const formatDateTimeZone = (date, format, timeZone) => {
     return dayjs(date).tz(timeZone).format(format);
}

// Used to validate between fields of formik validationSchema
export const validateFormik = (getValidationSchema, next = (f) => f, data = {}) => {
     const getErrorsFromValidationError = (validationError) => {
          const FIRST_ERROR = 0;
          return validationError.inner.reduce((errors, error) => {
               return {
                    ...errors,
                    [error.path]: error.errors[FIRST_ERROR],
               };
          }, {});
     };

     return (values) => {
          next();
          const validationSchema = getValidationSchema(values, data);

          try {
               validationSchema.validateSync(values, { abortEarly: false });
               return {};
          } catch (error) {
               return getErrorsFromValidationError(error);
          }
     };
};

// Get from the character index after first dash to the end of string.
// Example: activity_created_at => created_at or user_name => name
export const getAfterFirstDash = (str) => {
     let fieldName = str;
     const dashIndex = fieldName.indexOf('_');

     if (dashIndex !== -1) {
          return fieldName.substr(dashIndex + 1);
     }

     return fieldName;
};

export const deepCopyArray = (arr) => {
     return JSON.parse(JSON.stringify(arr));
};

// Turn thing 1 and 2 into json string and compare
export const jsonAndCompare = (thing1, thing2) => {
     return JSON.stringify(thing1) === JSON.stringify(thing2);
};

export const isEmpty = (array) => {
     let checkEmpty = false;
     array.some((element) => {
          if (element === '') {
               checkEmpty = true;
               return true;
          }
          checkEmpty = false;
          return false;
     });
     return checkEmpty;
};

export const isDuplicate = (array) => {
     for (let i = 0; i < array.length; i++) {
          for (let j = i + 1; j < array.length; j++) {
               if (array[i] === array[j]) {
                    return true;
               }
          }
     }
     return false;
};

export const checkDomain = (data) => {
     const domainFormat = /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/;
     let checkDomain = false;
     data.some((domain) => {
          if (!domainFormat.test(domain)) {
               checkDomain = true;
               return true;
          }
          checkDomain = false;
          return false;
     });
     return checkDomain;
};

// Track active toast messages
const activeToastMessages = new Set();

export const toastError = (response, options) => {
     if (!response.data.accountBlocked) {
          const message = response.data.message || response.data.error || response.statusText;
          
          // Check if this message is already being displayed
          if (activeToastMessages.has(message)) {
               return;
          }
          
          // Add message to active set
          activeToastMessages.add(message);
          
          // Show toast with onClose callback to remove from active set
          toast.error(message, {
               ...options,
               onClose: () => {
                    activeToastMessages.delete(message);
                    // Call original onClose if provided
                    if (options?.onClose) {
                         options.onClose();
                    }
               }
          });
     }
};

export const useOutsideHandling = (ref, clickOutside, eventListener = 'click', showPopupCreateCategories = false) => {
     React.useEffect(() => {
          const handleClickOutside = (event) => {
               if (showPopupCreateCategories) {
                    return;
               } else {
                    // Check if click is inside a react-select dropdown or any select/dropdown element
                    const target = event.target;
                    
                    // Check if parent element has class starting with 'cvr-step'
                    const hasCvrStepParent = target.closest('[class*="cvr-step"]');
                    
                    const isReactSelectElement = hasCvrStepParent && (
                         // React-select with classNamePrefix='react-select'
                         target.closest('.react-select__menu') || 
                         target.closest('.react-select__menu-portal') ||
                         target.closest('[class*="react-select"]') ||
                         // React-select without classNamePrefix (has css-xxxxx classes)
                         target.closest('[class*="-menu"]') ||
                         target.closest('[class*="-control"]') ||
                         target.closest('[class*="-option"]') ||
                         target.closest('[class*="-container"]') ||
                         // Check by role attributes (react-select uses these)
                         target.closest('[role="listbox"]') ||
                         target.closest('[role="option"]') ||
                         target.closest('[role="combobox"]')
                    );
                    
                    if (ref.current && !ref.current.contains(event.target) && !isReactSelectElement) {
                         clickOutside();
                    }
               }
          };

          const handlePressEsc = (event) => {
               if (ref.current && event.key === 'Escape') {
                    clickOutside();
               }
          };

          // Bind the event listener
          document.addEventListener(eventListener, handleClickOutside);
          document.addEventListener('keydown', handlePressEsc);
          return () => {
               // Unbind the event listener on clean up
               document.removeEventListener(eventListener, handleClickOutside);
               document.removeEventListener('keydown', handlePressEsc);
          };
     }, [ref, clickOutside]); // eslint-disable-line
};

export const getAnUrlParam = (href) => {
     let vars = {};
     const getParam = (m, key, value) => {
          vars[key.toLowerCase()] = value;
     };
     href.replace(/[?&]+([^=&]+)=([^&]*)/gi, getParam);

     return vars;
};

export const getLocalItem = (itemName) => {
     return JSON.parse(localStorage.getItem(itemName));
};

export const roundTo2DecimalPlaces = (number) => {
     return Math.round(number * 100) / 100;
};

export const numberWithCommas = (number) => {
     return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const tryParseJSON = (jsonString) => {
     try {
          if (jsonString && typeof jsonString === 'object') {
               return jsonString;
          }

          let o = JSON.parse(jsonString);
          if (o && typeof o === 'object') {
               return o;
          }
     } catch (e) {
          return false;
     }
     return false;
};

export const parseJson = (jsonString) => {
     if (typeof jsonString !== 'undefined' && jsonString !== '') {
          if (typeof jsonString === 'string') {
               try {
                    let object = JSON.parse(jsonString);

                    if (object && typeof object === 'object') {
                         return object;
                    }
               } catch (e) {
                    return null;
               }
          } else {
               return jsonString;
          }
     }
     return null;
};

export const tagPropType = PropTypes.oneOfType([
     PropTypes.func,
     PropTypes.string,
     PropTypes.shape({ $$typeof: PropTypes.symbol, render: PropTypes.func }),
     PropTypes.arrayOf(
          PropTypes.oneOfType([
               PropTypes.func,
               PropTypes.string,
               PropTypes.shape({
                    $$typeof: PropTypes.symbol,
                    render: PropTypes.func,
               }),
          ])
     ),
]);

export const useScrollTop = () => {
     useEffect(() => {
          window.scrollTo(0, 0);
     }, []);
};

export const validateEmail = (email) => {
     let emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

     return emailRegex.test(email);
};

export const getURLParams = (searchString = window.location.search.slice(1)) => {
     const params = {};
     const regex = /([^&=]+)=([^&]*)/g;
     let m = regex.exec(searchString);
     while (m) {
          params[decodeURIComponent(m[1])] = decodeURIComponent(m[2]);
          m = regex.exec(searchString);
     }

     return params;
};

export const getTimeZoneOffset = (timeZone) => {
     let offsetInMinutes = dayjs().tz(timeZone).$offset; // The time difference between UTC time and "timeZone", in minutes
     let sign = offsetInMinutes > 0 ? '+' : '-';
     let offsetInHours = Math.abs(offsetInMinutes / 60);
     let minutes = (offsetInHours % 1) * 60;
     offsetInHours = Math.trunc(offsetInHours);
     let offsetInHoursStr = offsetInHours.toString();
     let theLast2Digits = minutes === 0 ? '00' : minutes.toString();

     if (offsetInHoursStr.length === 1) {
          offsetInHoursStr = `${sign}0${offsetInHoursStr}:${theLast2Digits}`;
     } else {
          offsetInHoursStr = `${sign}${offsetInHoursStr}:${theLast2Digits}`;
     }

     return `GMT${offsetInHoursStr}`;
};

export const base64Decode = (dataEncode) => {
     return JSON.parse(Buffer.from(dataEncode, 'base64').toString('ascii'));
};

export const guidGenerator = () => {
     const S4 = function () {
          return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
     };
     return S4() + S4() + '-' + S4() + '-' + S4() + '-' + S4() + '-' + S4() + S4() + S4();
};

/**
 * Sanitize a value to prevent CSV formula injection attacks.
 * Prefixes dangerous characters with a single quote to prevent spreadsheet
 * applications from interpreting the value as a formula.
 * @param {string} value - The value to sanitize
 * @returns {string} - Sanitized value
 */
export const sanitizeCsvValue = (value) => {
     if (typeof value !== 'string' || value.length === 0) {
          return value;
     }
     // Characters that can trigger formula execution in spreadsheet applications
     const dangerousChars = ['=', '+', '-', '@', '	', ''];
     const firstChar = value.charAt(0);
     if (dangerousChars.includes(firstChar)) {
          return "'" + value;
     }
     return value;
};

export const exportToCSV = (filename, rows) => {
     let processRow = function (row) {
          let finalVal = '';
          for (let j = 0; j < row.length; j++) {
               if (row[j]) {
                    let innerValue = row[j] === null ? '' : row[j].toString();
                    if (row[j] instanceof Date) {
                         innerValue = row[j].toLocaleString();
                    }
                    // Sanitize value to prevent CSV formula injection
                    innerValue = sanitizeCsvValue(innerValue);
                    let result = innerValue.replace(/"/g, '""');
                    if (result.search(/("|,|\n)/g) >= 0) result = '"' + result + '"';
                    if (j > 0) finalVal += ',';
                    finalVal += result;
               }
          }
          return finalVal + '\n';
     };

     let csvFile = '';

     for (const row of rows) {
          csvFile += processRow(row);
     }

     // https://stackoverflow.com/questions/17879198/adding-utf-8-bom-to-string-blob
     // Prepend \ufeff to the csvFile to prevent special characters from being updated to other characters
     let blob = new Blob(['\ufeff' + csvFile], {
          type: 'text/csv;charset=utf-8;',
     });

     if (navigator.msSaveBlob) {
          // IE 10+
          navigator.msSaveBlob(blob, filename);
     } else {
          let link = document.createElement('a');
          if (link.download !== undefined) {
               // feature detection
               // Browsers that support HTML5 download attribute
               let url = URL.createObjectURL(blob);
               link.setAttribute('href', url);
               link.setAttribute('download', filename);
               link.style.visibility = 'hidden';
               document.body.appendChild(link);
               link.click();
               document.body.removeChild(link);
          }
     }
};
export const exportToCSV2 = (csvFile, filename) => {
     // https://stackoverflow.com/questions/17879198/adding-utf-8-bom-to-string-blob
     // Prepend \ufeff to the csvFile to prevent special characters from being updated to other characters
     let blob = new Blob(['\ufeff' + csvFile], {
          type: 'text/csv;charset=utf-8;',
     });

     if (navigator.msSaveBlob) {
          // IE 10+
          navigator.msSaveBlob(blob, filename);
     } else {
          let link = document.createElement('a');
          if (link.download !== undefined) {
               // feature detection
               // Browsers that support HTML5 download attribute
               let url = URL.createObjectURL(blob);
               link.setAttribute('href', url);
               link.setAttribute('download', filename);
               link.style.visibility = 'hidden';
               document.body.appendChild(link);
               link.click();
               document.body.removeChild(link);
          }
     }
};

export const dateDiffIndays = (date1, date2) => {
     let dt1 = new Date(date1);
     let dt2 = new Date(date2);
     return Math.floor(
          (Date.UTC(dt2.getFullYear(), dt2.getMonth(), dt2.getDate(), dt2.getHours()) -
               Date.UTC(dt1.getFullYear(), dt1.getMonth(), dt1.getDate(), dt1.getHours())) /
          (1000 * 60 * 60 * 24)
     );
};

export const dateDiffInseconds = (date1, date2) => {
     let dt1 = new Date(date1);
     let dt2 = new Date(date2);
     return Math.floor((dt2.getTime() - dt1.getTime()) / 1000);
};

export const strToClass = (str) => {
     return str.toLowerCase().replace(/ /g, '-');
};

export const removeURLParams = () => {
     window.history.replaceState({}, document.title, window.location.origin + window.location.pathname);
};

export const getHostName = () => {
     let _urlApi = process.env.REACT_APP_DATALAYER_APP_DOMAIN;
     return _urlApi;
};

export const getLinkCertificated = () => {
     let _urlApi = process.env.REACT_APP_CERTIFICATED_URL;
     return _urlApi;
};

export const getLinkBaseActiveCampaign = () => {
     let baseUrl = process.env.REACT_APP_ACTIVE_HOSTED;
     return baseUrl;
};

export const secondsToTime = (seconds) => {
     let time_left_hours = Math.floor(seconds / (60 * 60));
     time_left_hours = time_left_hours < 10 ? `0${time_left_hours}` : time_left_hours;

     let divisor_for_minutes = seconds % (60 * 60);
     let time_left_minutes = Math.floor(divisor_for_minutes / 60);
     time_left_minutes = time_left_minutes < 10 ? `0${time_left_minutes}` : time_left_minutes;

     let divisor_for_seconds = divisor_for_minutes % 60;
     let time_left_seconds = Math.ceil(divisor_for_seconds);
     time_left_seconds = time_left_seconds < 10 ? `0${time_left_seconds}` : time_left_seconds;

     return {
          h: time_left_hours,
          m: time_left_minutes,
          s: time_left_seconds,
          time: seconds,
     };
};

export const formatUserDate = (date) => {
     const dateObject = new Date(date);
     const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
     const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
     let dayOfWeek = dayNames[dateObject.getDay()];
     let nameOfMonth = monthNames[dateObject.getMonth()];
     let daySuffix = 'th';
     let dayOfMonth = dateObject.getDate();
     switch (dayOfMonth) {
          case 1:
          case 21:
          case 31:
               daySuffix = 'st';
               break;
          case 2:
          case 22:
               daySuffix = 'nd';
               break;
          case 3:
          case 23:
               daySuffix = 'rd';
               break;
          default:
               daySuffix = 'th';
               break;
     }

     return `${dayOfWeek}, ${nameOfMonth} ${dayOfMonth}${daySuffix}`;
};

export const formatTimeOfficeHours = (start, end) => {
     const startDate = new Date(start);
     const endDate = new Date(end);
     const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
     const dayOfMonth = (startDate.getDate() < 10 ? '0' : '') + startDate.getDate();
     const month = monthNames[startDate.getMonth()];
     const year = startDate.getFullYear();
     const hoursStart = startDate.getHours();
     const hoursEnd = endDate.getHours();

     return {
          date: `${dayOfMonth} ${month} ${year}`,
          hours: `${hoursStart}h - ${hoursEnd}h`,
     };
};

export const initializeGTM = () => {
     if (!document.querySelector('script[src*="https://www.googletagmanager.com/gtm.js"]')) {
          (function (w, d, s, l, i) {
               w[l] = w[l] || [];
               w[l].push({
                    'gtm.start': new Date().getTime(),
                    event: 'gtm.js',
               });
               let f = d.getElementsByTagName(s)[0],
                    j = d.createElement(s),
                    dl = l !== 'dataLayer' ? '&l=' + l : '';
               j.async = true;
               j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
               f.parentNode.insertBefore(j, f);
          })(window, document, 'script', 'dataLayer', process.env.REACT_APP_GTM_CONTAINER_ID);
     }
};

export const gtmTrackingIdentifyLLUser = (userDetails, accountDetails) => {
     let data = {
          event: 'identifyLLUser',
     };

     if (userDetails) {
          data.userDetails = userDetails;
     }

     if (accountDetails) {
          data.accountDetails = accountDetails;
     }

     window.dataLayer = window.dataLayer || [];
     window.dataLayer.push(data);
     initializeGTM();
};

export const gtmTrackingChangeLLAccount = (userDetails, accountDetails) => {
     let data = {
          event: 'changeLLAccount',
     };

     if (userDetails) {
          data.userDetails = userDetails;
     }

     if (accountDetails) {
          data.accountDetails = accountDetails;
     }

     window.dataLayer = window.dataLayer || [];
     window.dataLayer.push(data);
     initializeGTM();
};

export const removeHash = () => {
     window.history.replaceState('', document.title, window.location.pathname + window.location.search);
};

export const cleanURL = () => {
     window.history.replaceState('', document.title, window.location.pathname);
};

export const removeHTMLFromString = (str) => {
     return str.replace(/<[^>]*>?/gm, '');
};

export const swap2ElementsOfArr = (arr, position1, position2) => {
     return ([arr[position1], arr[position2]] = [arr[position2], arr[position1]]);
};

export const includeLowercase = (str1, str2) => {
     return str1.toLowerCase().includes(str2.toLowerCase());
};

export const moveElementToFrontArr = (arr, elementIndex) => {
     return arr.unshift(arr.splice(elementIndex, 1)[0]);
};

export const getUnlimitedNumber = (number) => {
     return number === -1 ? 'unlimited' : `${number}`;
};
export const getDataChild = (dataChild, key, keyName) => {
     if (typeof dataChild[key] === 'object' && dataChild[key] !== null) {
          let _path = keyName !== '' ? `${keyName}.${key}` : key;
          const flag = Object.keys(dataChild[key]).some((child) => {
               if (!dataChild[key][child]) return false;
               if (typeof dataChild[key][child] === 'object') {
                    return true;
               }
               return false;
          });

          if (flag) {
               return {
                    title: key,
                    path: _path,
                    fullCode: dataChild[key],
                    hasChild: true,
                    childs: [],
               };
          }

          if (Object.keys(dataChild[key]).length > 0) {
               return {
                    title: key,
                    path: _path,
                    fullCode: dataChild[key],
                    hasChild: false,
               };
          }
          return null;
     }

     return {
          title: key,
          path: keyName !== '' ? `${keyName}.${key}` : key,
          fullCode: { [key]: dataChild[key] },
          hasChild: false,
     };
};

export const parseEventDataLv = (data, keyName) => {
     const _arrayLv = [];

     for (let key in data) {
          const _dataResult = getDataChild(data, key, keyName);

          if (_dataResult?.hasChild) {
               const _childArrayLv = parseEventDataLv(data[key], keyName !== '' ? `${keyName}.${key}` : key);

               _dataResult.childs = _childArrayLv;
               _arrayLv.push(_dataResult);
          } else if (_dataResult) {
               _arrayLv.push(_dataResult);
          }
     }
     return _arrayLv;
};

export const getDLScript = (accountId) => {
     let suffix = '',
          isAmd = '',
          amdVariable = '';

     // https://rubberb.com/
     const ids = ['663a5206-c41e-42fd-8cae-1a21cd59e270'];

     switch (process.env.REACT_APP_ENV) {
          case 'production':
               suffix = '';
               break;
          default:
               suffix = '-' + process.env.REACT_APP_ENV + '-datalayer';
               break;
     }

     if (ids.some((id) => id === accountId)) {
          isAmd = '.amd';
          amdVariable = 'var isAmd=true;';
     }

     return `<!-- ListenLayer -->
<script type="text/javascript">${amdVariable}(function(c,a,d,b,e) {c[b] = c[b] || [];
c=a.getElementsByTagName(d)[0];a=a.createElement(d);b="dataLayer"!= b ?"\x26l\x3d"+b:"";a.async = !0;
a.src = "https://assets${suffix}.listenlayer.com/datalayer${isAmd}.min.js?id\x3d"+e+b;
c.parentNode.insertBefore(a,c)})(window,document,"script","dataLayer",
"${accountId}");</script>
<!-- End ListenLayer -->`;
};

export const getSessionKeySignupListeners = (accountId) => {
     return `llSignupListeners_${accountId}`;
};

export const getSessionKeySignupDestinations = (accountId) => {
     return `llSignupDestination_${accountId}`;
};

export const getSessionKeySignupCurrentStep = (accountId) => {
     return `llSignupCurrentStep_${accountId}`;
};

export const removeDuplicateValueInArray = (array) => {
     if (!array) return [];

     const newArray = array.filter((item, index) => {
          return array.findIndex((_item) => JSON.stringify(_item) === JSON.stringify(item)) === index;
     });

     return newArray;
};

export const getStaticJSFile = (accountId) => {
     let suffix = '';

     switch (process.env.REACT_APP_ENV) {
          case 'production':
               suffix = '';
               break;
          default:
               suffix = '-' + process.env.REACT_APP_ENV;
               break;
     }
     return `https://static${suffix}.listenlayer.com/${accountId}.json`;
};

export const handleToggleSidebar = ({
     activeMainNavItem = null,
     toggleMinimizeNav = false,
     forceMinimizeNav = null,
     toggleShowNavChild = false,
     forceShowNavChild = null,
}) => {
     const sidebar = document.querySelector('.sidebar-v2');
     const wrapper = document.querySelector('.c-wrapper.v2');
     const sidebarLogos = document.querySelectorAll('.sidebar-v2 .sidebar-top .sidebar-brand-full');
     const headerToggle = document.querySelector('.c-wrapper.v2 .toggle-sidebar');
     const sidebarToggle = document.querySelector('.sidebar-v2 .toggle-sidebar');     

     if (sidebar) {
          const sidebarClassList = sidebar.classList;
          const checkSidebarMinimize = sidebarClassList.contains('minimize-nav');
          const checkSidebarShowNavChild = sidebarClassList.contains('show-nav-child');
          const currentSidebarMinimize =
               forceMinimizeNav !== null ? forceMinimizeNav : toggleMinimizeNav ? !checkSidebarMinimize : checkSidebarMinimize;

          let currentSidebarShowNavChild;
          if (forceShowNavChild !== null) {
               currentSidebarShowNavChild = forceShowNavChild;
          } else if (toggleShowNavChild) {
               currentSidebarShowNavChild = !checkSidebarShowNavChild;
          } else {
               currentSidebarShowNavChild = checkSidebarShowNavChild;
          }

          const hasNavChild = !!activeMainNavItem && activeMainNavItem !== subscriberNavKey.INSIGHTS;

          if (toggleMinimizeNav) {
               if (currentSidebarMinimize) {
                    sidebarClassList.add('minimize-nav');
               } else {
                    sidebarClassList.remove('minimize-nav');
               }

               if (wrapper) {
                    const wrapperClasList = wrapper.classList;
                    if (currentSidebarMinimize) {
                         wrapperClasList.add('minimize-nav');
                    } else {
                         wrapperClasList.remove('minimize-nav');
                    }
               }
          }

          if (toggleShowNavChild) {
               if (currentSidebarShowNavChild) {
                    sidebarClassList.add('show-nav-child');
               } else {
                    sidebarClassList.remove('show-nav-child');
               }

               if (wrapper) {
                    const wrapperClasList = wrapper.classList;
                    if (currentSidebarShowNavChild) {
                         wrapperClasList.add('show-nav-child');
                    } else {
                         wrapperClasList.remove('show-nav-child');
                    }
               }
          }

          if (headerToggle) {
               const headerToggleClasList = headerToggle.classList;

               if (currentSidebarMinimize && !currentSidebarShowNavChild) {
                    headerToggleClasList.remove('hide');
                    headerToggleClasList.add('toggler-right');
               } else {
                    headerToggleClasList.add('hide');
               }
          }

          if (sidebarToggle) {
               const sidebarToggleClasList = sidebarToggle.classList;

               if (currentSidebarMinimize && !currentSidebarShowNavChild && !hasNavChild) {
                    sidebarToggleClasList.add('hide');
               } else {
                    sidebarToggleClasList.remove('hide');
               }

               if (!currentSidebarShowNavChild) {
                    sidebarToggleClasList.add('toggler-right');
               } else {
                    sidebarToggleClasList.remove('toggler-right');
               }
          }

          if (sidebarLogos) {
               sidebarLogos.forEach((sidebarLogo) => {
                    const sidebarLogoClass = sidebarLogo.classList;
                    sidebarLogoClass.add('hide');

                    if (currentSidebarMinimize) {
                         if (sidebarLogoClass.contains('minimize-nav')) {
                              sidebarLogoClass.remove('hide');
                         }
                    } else if (sidebarLogoClass.contains('full-logo')) {
                         sidebarLogoClass.remove('hide');
                    }
               });
          }
     }
};

export const capitalizeFirstLetter = (string) => {
     if (!string) return '';
     return string.charAt(0).toUpperCase() + string.slice(1);
};

export const capitalizeFirstLetterEachWords = (string) => {
     return string
          .toLowerCase()
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
};

export const resizeSidebarModal = (defaultSize) => {
     const sidebarModal = document.querySelector('.sidebar-modal');
     const sidebarModalDragHandles = document.querySelectorAll('.sidebar-modal .flexible-resize');
     sidebarModalDragHandles.forEach((sidebarModalDragHandle) => {
          const sidebarFlexibleModal = sidebarModalDragHandle.closest('.ll-flexible-modal');

          if (defaultSize) {
               sidebarFlexibleModal.style.maxWidth = `${defaultSize}px`;
          }

          const startDrag = (e) => {
               sidebarModal.classList.remove('ll-flexible-modal--animated');
               sidebarModal.classList.add('ll-flexible-modal--pulling');
               document.documentElement.addEventListener('mousemove', doDrag, false);
               document.documentElement.addEventListener('mouseup', stopDrag, false);
          };

          const doDrag = (e) => {
               const dragWidth = window.innerWidth - e.clientX;
               if (dragWidth > window.innerWidth - 80) {
                    sidebarFlexibleModal.style.maxWidth = `${window.innerWidth - 80}px`;
               } else if (dragWidth < window.innerWidth * 0.6) {
                    sidebarFlexibleModal.style.maxWidth = `${window.innerWidth * 0.6}px`;
               } else {
                    sidebarFlexibleModal.style.maxWidth = `${dragWidth}px`;
               }
          };

          const stopDrag = (e) => {
               document.documentElement.removeEventListener('mousemove', doDrag, false);
               document.documentElement.removeEventListener('mouseup', stopDrag, false);
               sidebarModal.classList.add('ll-flexible-modal--animated');
               sidebarModal.classList.remove('ll-flexible-modal--pulling');
          };

          sidebarModalDragHandle.addEventListener('mousedown', startDrag, false);
     });
};

export const convertReportDuration = (totalSeconds) => {
     let sec_num = parseInt(totalSeconds, 10);
     let hours = Math.floor(sec_num / 3600);
     let minutes = Math.floor(sec_num / 60) % 60;
     let seconds = sec_num % 60;

     return hours > 0 ? `${hours}h ${minutes}m ${seconds}s` : `${minutes}m ${seconds}s`;
};
export const convertReportSeconds = (time) => {
     let newTime = 0;
     const timeArr = time.split(' ');
     if (timeArr.length > 0) {
          timeArr.forEach((item) => {
               if (item.indexOf('h') !== -1) {
                    const hours = parseInt(item.slice(0, item.indexOf('h')));
                    if (Number.isFinite(hours)) {
                         newTime = newTime + hours * 3600;
                    }
               }
               if (item.indexOf('m') !== -1) {
                    const minutes = parseInt(item.slice(0, item.indexOf('m')));
                    if (Number.isFinite(minutes)) {
                         newTime = newTime + minutes * 60;
                    }
               }
               if (item.indexOf('s') !== -1) {
                    const seconds = parseInt(item.slice(0, item.indexOf('s')));
                    if (Number.isFinite(seconds)) {
                         newTime = newTime + seconds;
                    }
               }
          });
     }
     if (newTime === 0) {
          newTime = time;
     }
     return newTime;
};

export const encodeObj = (obj) => {
     return btoa(encodeURIComponent(JSON.stringify(obj)));
};

export const decodeObj = (obj) => {
     return tryParseJSON(decodeURIComponent(atob(obj)));
};

if (window.location.search.includes('show-server-side=1')) {
     localStorage.setItem('show-server-side', '1');
}

if (window.location.search.includes('revealed-company=1')) {
     localStorage.setItem('llRevealedCompany', '1');
}

export const checkEnableRevealCompany = () => {
     if (window.location.search.includes('revealed-company=1') || localStorage.getItem('llRevealedCompany')) {
          return true;
     }
     return false;
};

export const generateId = (string) => {
     const random = (Math.floor(Math.random() * 5555) + 1111).toString().substring(0, 4);
     if (!string) {
          return random;
     }
     return string + random;
};

export const getIssueFromCategories = (issue) => {
     switch (issue) {
          case 'isUncategorized':
               return 'Form is uncategorized';
          case 'isHasNoListener':
               return 'Form has no Listener';
          case 'isAttention':
               return 'Needs attention';
          case 'isDrafted':
               return 'Changes drafted';
          case 'isIgnored':
               return 'Ignored';
          case 'isNoAction':
               return 'No action required';
          case 'isCategorized':
               return 'Already categorized';
          default:
               return '';
     }
};

export const getFromValueNameKey = (data) => {
     if (data.formId) return 'id';
     if (data.formClass) return 'class';
     if (data.formName) return 'name';
     if (data.formAction) return 'action';
     if (data.formMethod) return 'method';
};

export function checkConditionOperator(formValue, value, operator) {
     let status = false;
     switch (operator) {
          case 'em': // Exactly matches
               status = formValue === value;
               break;
          case 'ct': // Contains
               status = formValue.includes(value);
               break;
          case 'nct': // does not contain
               status = !formValue.includes(value);
               break;
          case 'bw': // Begins with
               status = formValue.startsWith(value);
               break;
          case 'ew': // Ends with
               status = formValue.endsWith(value);
               break;
          default:
               status = false;
               break;
     }
     return status;
}

export function isMatchCustomForm(form, targetRules) {
     let matchRules = false;

     const checkRuleCondition = ({ condition, j }) => {
          const length = Object.keys(condition).length;
          let status = false;

          if (j < length) {
               const _object = condition['g' + j] || condition;
               let valuesToCompares = [];

               if (_object.attribute === 'CSS Class' && typeof form.formClass === 'string') {
                    valuesToCompares = form.formClass.split(' ');
               }

               if (_object.attribute === 'CSS ID' && typeof form.formId === 'string') {
                    valuesToCompares = form.formId.split(' ');
               }

               if (_object.attribute === 'Name') {
                    const formName = form.formName;

                    if (typeof formName === 'string') {
                         valuesToCompares = formName.split(' ');
                    }
               }

               if (_object.operator === 'nct') {
                    // Not contain
                    if (valuesToCompares.length === 0) {
                         status = true;
                    } else {
                         valuesToCompares.every((valueToCompare) => {
                              if (valueToCompare) {
                                   status = !valueToCompare.toLowerCase().includes(_object.value.toLowerCase());
                              }

                              return status;
                         });
                    }
               } else {
                    valuesToCompares.some((valueToCompare) => {
                         if (valueToCompare) {
                              switch (_object.operator) {
                                   case 'em': // Exactly matches
                                        status = valueToCompare && valueToCompare === _object.value;
                                        break;
                                   case 'ct': // Contains
                                        status = valueToCompare.toLowerCase().includes(_object.value.toLowerCase());
                                        break;
                                   case 'bw': // Begins with
                                        status = valueToCompare.toLowerCase().startsWith(_object.value.toLowerCase());
                                        break;
                                   case 'ew': // Ends with
                                        status = valueToCompare.toLowerCase().endsWith(_object.value.toLowerCase());
                                        break;
                                   default:
                                        status = false;
                                        break;
                              }
                         }
                         return status;
                    });
               }

               if ((!status && _object.operator !== 'nct') || (status && _object.operator === 'nct')) {
                    if (_object.attribute === 'CSS Class' && form.formClass) {
                         status = checkConditionOperator(form.formClass, _object.value, _object.operator);
                    }

                    if (_object.attribute === 'CSS ID' && form.formId) {
                         status = checkConditionOperator(form.formId, _object.value, _object.operator);
                    }
               }

               if (typeof _object.conjunction !== 'undefined' && _object.conjunction === 'and') {
                    j = j + 1;
                    return status && checkRuleCondition({ condition, j });
               } else if (typeof _object.conjunction !== 'undefined' && _object.conjunction === 'or') {
                    j = j + 1;
                    return checkRuleCondition({ condition, j }) || status;
               }
          }
          return status;
     };

     const checkRuleConditions = ({ conditions, i }) => {
          const status = checkRuleCondition({ condition: conditions[i], j: 0 });

          if (i < conditions.length) {
               if (typeof conditions[i + 1] !== 'undefined' && conditions[i].conjunction && conditions[i].conjunction === 'and') {
                    i = i + 1;

                    return checkRuleConditions({ conditions, i }) && status;
               } else if (typeof conditions[i + 1] !== 'undefined' && conditions[i].conjunction && conditions[i].conjunction === 'or') {
                    i = i + 1;
                    return checkRuleConditions({ conditions, i }) || status;
               }
               return status;
          }
          return status;
     };

     matchRules = checkRuleConditions({ conditions: targetRules, i: 0 });
     return matchRules;
}

export const useMediaQuery = (minWidth) => {
     const [state, setState] = useState({
          windowWidth: window.innerWidth,
          isDesiredWidth: window.innerWidth < minWidth,
     });

     useEffect(() => {
          const resizeHandler = () => {
               const currentWindowWidth = window.innerWidth;
               const isDesiredWidth = currentWindowWidth < minWidth;
               setState({ windowWidth: currentWindowWidth, isDesiredWidth });
          };
          window.addEventListener('resize', resizeHandler);
          return () => window.removeEventListener('resize', resizeHandler);
     }, [state.windowWidth]); //eslint-disable-line

     return state.isDesiredWidth;
};

export const camelCase = (name) => {
     return `${_.camelCase(name)}`;
};
export const filterObjectValuesNotNull = (obj) => {
     const result = {};

     for (const key in obj) {
          const value = obj[key];

          if (value !== null) {
               result[key] = value;
          }
     }

     return result;
};

export const camelCaseCustomProperties = (value) => {
     if (!value) return value;
     return `${camelCase(
          value
               .replace(/\b\w/g, (match) => match.toUpperCase())
               .replace(/^\w/, (match) => match.toLowerCase())
               .replace(/\s+/g, '')
     )}Custom`;
};

export const formatStringtoNumber = (string) => {
     const arr = string.split(',');
     const newArr = [];

     arr.forEach((ar) => {
          let item = ar.replace(/[^\d.,]/g, ''); // Remove the dollar sign
          item = item.replace(/\B(?=(\d{3})+(?!\d))/g, ','); // Add commas
          newArr.push(item);
     });

     const result = newArr.join(',');
     return result;
};
export const clearConversionDraft = (id) => {
     for (let key in sessionStorage) {
          if (key.startsWith(`conversionDraft_${id}`)) {
               sessionStorage.removeItem(key);
          }
     }
};

export const generateFilterFromNewFilter = ({ filter }) => {
     const newFilterv2 = [];
     for (let i = 0; i < filter.length; i++) {
          const item = filter[i];
          let newItem = {};
          for (const key in item) {
               if (key !== 'conjunction') {
                    newItem = { ...newItem, [key]: item[key] };
                    newFilterv2.push({
                         ...item[key],
                    });
               }
          }
     }
     return newFilterv2;
};

export const generateNewFilterFromOldFilter = ({ filter }) => {
     let newItem = {
          conjunction: '',
     };
     for (let i = 0; i < filter.length; i++) {
          newItem = { ...newItem, [`g${i}`]: { ...filter[i], conjunction: 'and' } };
     }
     return [{ ...newItem }];
};

function formatNumberWithCommas(number) {
     return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') !== 'NaN' ? number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : 'None';
}

export const formatNumber = (value) => {
     if (typeof value !== 'number') {
          return String(value);
     } else {
          return formatNumberWithCommas(Number(value));
     }
};

export const formattedDate = (date) => {
     return dayjs(date).format('YYYY-MM-DD');
};

export const getShortDomain = (domain) => {
     return domain.replace('https://', '').replace('http://', '').replace(/\/$/, '').replace('www.', '');
};

export const BROWSER_VARIABLES_KEY = {
     localStorage: 'Local Storage',
     sessionStorage: 'Session Storage',
     cookie: 'Cookie Storage',
};

export const RoundFirstSignificantDigit = (num) => {
     function decimalToDigitsArray(decimalNumber) {
          const digitString = decimalNumber.toString().replace('.', '');
          const digitArray = digitString.split('').map(Number);
          return digitArray;
     }
     function getMultiplierNumber(number) {
          const binaryString = parseInt('1' + '0'.repeat(number));
          return binaryString;
     }
     let result = 0;
     const arrNumber = decimalToDigitsArray(num);
     let numberZero = 0;
     if (Math.floor(num) === 0) {
          for (let i = 0; i < arrNumber.length; i++) {
               if (arrNumber[i] !== 0) {
                    numberZero = i;
                    break;
               }
          }
     }
     if (numberZero > 3) {
          result = Math.floor(num * getMultiplierNumber(numberZero)) / getMultiplierNumber(numberZero);
     } else {
          result = Math.round(num * 10000) / 10000;
     }
     return result;
};

export const convertJsonToCSV = (rows) => {
     let processRow = function (row) {
          let finalVal = '';
          for (let j = 0; j < row.length; j++) {
               let innerValue = row[j] === null ? '' : row[j].toString();
               if (row[j] instanceof Date) {
                    innerValue = row[j].toLocaleString();
               }
               // Sanitize value to prevent CSV formula injection
               innerValue = sanitizeCsvValue(innerValue);
               let result = innerValue.replace(/"/g, '""');
               if (result.search(/("|,|\n)/g) >= 0) result = '"' + result + '"';
               if (j > 0) finalVal += ',';
               finalVal += result;
          }
          return finalVal + '\n';
     };

     let csvFile = '';

     for (const row of rows) {
          csvFile += processRow(row);
     }
     return csvFile;
};

export const getDataExportCompare = ({ compareData, dimensions, metrics }) => {
     const returnRows = [];
     compareData.forEach((row) => {
          Object.entries(row).forEach((value) => {
               let fieldValues = {};
               switch (value[0]) {
                    case 'dimensionCompoundValues':
                         fieldValues = value[1].reduce((prevVal, currentVal, i) => {
                              if (i <= dimensions.length - 1) {
                                   return { ...prevVal, [dimensions[i]]: { value: currentVal.value } };
                              } else {
                                   return {};
                              }
                         }, {});
                         returnRows.push(fieldValues);
                         break;
                    case 'metricCompoundValues':
                         Object.entries(value[1]).forEach((item) => {
                              let dateValue = {};
                              dimensions.forEach((dimension, index) => {
                                   if (index === 0) {
                                        dateValue = { ...dateValue, ...{ [dimension]: { value: JSON.stringify(item[0]) } } };
                                   } else {
                                        dateValue = { ...dateValue, ...{ [dimension]: { value: '' } } };
                                   }
                              });
                              fieldValues = item[1].reduce((prevVal, currentVal, i) => {
                                   if (i <= metrics.length - 1) {
                                        if (isRateField({ value: metrics[i] })) {
                                             return {
                                                  ...prevVal,
                                                  [metrics[i]]: { value: divideBy100(currentVal.value) },
                                             };
                                        } else {
                                             return { ...prevVal, [metrics[i]]: { value: currentVal.value } };
                                        }
                                   } else {
                                        return {};
                                   }
                              }, {});
                              if (Object.entries(fieldValues).length === 0) {
                                   metrics.forEach((metric) => {
                                        fieldValues = { ...fieldValues, ...{ [metric]: { value: 0 } } };
                                   });
                              }
                              returnRows.push({ ...dateValue, ...fieldValues });
                         });
                         break;
                    default:
                         break;
               }
          });
     });

     return returnRows;
};

const divideBy100 = (value) => {
     let strValue = value.toString();

     // Nếu không có dấu thập phân, thêm ".00"
     if (!strValue.includes('.')) {
          strValue += '.00';
     }

     // Loại bỏ dấu thập phân để làm việc với toàn số
     const raw = strValue.replace('.', '');
     // Vị trí dấu thập phân mới = vị trí cũ - 2
     const newDecimalIndex = strValue.indexOf('.') - 2;

     let resultStr;
     if (newDecimalIndex <= 0) {
          // Thêm "0." và padding số 0
          resultStr = '0.' + '0'.repeat(Math.abs(newDecimalIndex)) + raw;
     } else {
          resultStr = raw.slice(0, newDecimalIndex) + '.' + raw.slice(newDecimalIndex);
     }

     return parseFloat(resultStr);
};

export const getMetrics = ({ segments, metrics }) => {
     let newMetrics = [];

     if (segments?.length > 0) {
          metrics.forEach((metric) => {
               segments.forEach((segment) => {
                    newMetrics.push(`${getSegmentField(segment.id, metric)}`);
               });
          });
     } else {
          newMetrics = [...metrics];
     }

     return newMetrics;
};
export const getHeaderReport = ({ headerColumn, segments, filterFields, isCompare, newDateRange }) => {
     const headerSegment = [];
     const nameHeader = [];
     const annotationRow = [];
     let metrics = isCompare ? filterFields.metrics.flatMap((item) => [item, `${item}_1`]) : filterFields.metrics;
     let newHeader = [...headerColumn];
     let compareDay = [];
     const compareDayRow = [];
     if (isCompare) {
          compareDay.push(
               `${dayjs(newDateRange.selection.startDate).format('MMM D, YYYY')} - ${dayjs(newDateRange.selection.endDate).format('MMM D, YYYY')}`,
               `${dayjs(newDateRange.compare.startDate).format('MMM D, YYYY')} - ${dayjs(newDateRange.compare.endDate).format('MMM D, YYYY')}`
          );
     }
     headerColumn.forEach((element, index) => {
          if (segments?.length > 0) {
               if (element.type ==='dimension' || (element.type ==='property' && element.value !== 'viewDetail')) {
                    nameHeader.push(element.label);
                    headerSegment.push('');
                    annotationRow.push('');
                    compareDayRow.push('');
               }
          } else if (element.value !== 'viewDetail') {
               nameHeader.push(element.label);
               if (isCompare && filterFields.metrics.includes(element.value)) {
                    const indexCompare = newHeader.findIndex((item) => item.value === element.value);

                    nameHeader.push(element.label);

                    compareDay.forEach((day, index) => {
                         if (!annotationRow.some((row) => row.includes(day))) {
                              annotationRow.push(`D${index + 1}: ${day}`);
                         }
                         compareDayRow.push(`D${index + 1}`);
                    });

                    if (indexCompare !== -1) {
                         newHeader.splice(indexCompare + 1, 0, { label: element.label, value: `${element.value}_1` });
                    }
               } else {
                    annotationRow.push('');
                    compareDayRow.push('');
               }
          }
     });

     if (segments?.length > 0) {
          newHeader.forEach((header) => {
               segments.forEach((segment, index) => {
                    if (metrics.find((el) => el === header.value)) {
                         nameHeader.push(header.label);
                         newHeader.push({ label: header.label, value: `${getSegmentField(segment.id, header.value)}` });

                         if (annotationRow.filter((element) => element === 'Metrics').length !== index + 1) {
                              compareDay.forEach((day, index) => {
                                   if (!annotationRow.some((row) => row.includes(day))) {
                                        annotationRow.push(`D${index + 1}: ${day}`);
                                   }
                                   compareDayRow.push(`D${index + 1}`);
                              });
                              if (!annotationRow.some((el) => el === `S${index + 1}:${segment.name}`)) {
                                   annotationRow.push(`S${index + 1}:${segment.name}`);
                              }
                              headerSegment.push(`S${index + 1}`);
                         } else {
                              annotationRow.push('');
                              headerSegment.push('');
                         }
                         if (isCompare) {
                              newHeader.push({ label: header.label, value: `${getSegmentField(segment.id, header.value)}_1` });
                              nameHeader.push(header.label);
                              headerSegment.push('');
                         }
                    }
               });
          });
          newHeader = newHeader.filter((header) => header.value !== metrics.find((el) => el === header.value));
     }

     return { headerSegment, nameHeader, annotationRow, newHeader, compareDayRow };
};

export const generateCustomDimensionName = (name) => {
     return `${camelCase(
          name
               .replace(/\b\w/g, (match) => match.toUpperCase())
               .replace(/^\w/, (match) => match.toLowerCase())
               .replace(/\s+/g, '')
     )}Custom`
}

export const exportVisibleReport = ({
     data,
     filterFields,
     newProperties,
     newMetric,
     headerColumn,
     reportName,
     reportCompare,
     isCompare,
     reportType,
     isCustom = false,
     segments,
     newDateRange,
     intentProperties,
     customProperties = [],
}) => {
     let dataReport = [...data.res];
     const indexFilter = {
          indexMetric: [],
          indexProperties: [],
     };
     const fieldReport = { ...filterFields };
     const propertyType = isCustom ? `propertiesCompoundValues` : `propertyCompoundValues`;
     // remove metrics and properties with old data
     let metrics = segments?.length > 0 ? getMetrics({ segments, metrics: newMetric }) : newMetric;
     let properties = newProperties;
     if (dataReport?.length > 0 && dataReport[0].metricCompoundValues.length < metrics.length) {
          // reload api call temp report
          metrics = segments?.length > 0 ? getMetrics({ segments, metrics: filterFields.metrics }) : filterFields.metrics;
     }
     if (dataReport?.length > 0 && dataReport[0][propertyType].length < properties.length) {
          // reload api call temp report
          properties = filterFields.properties;
     }
     if (segments?.length > 0) {
          fieldReport.metrics = getMetrics({ segments, metrics: filterFields.metrics });
     }
     if (isCompare) {
          metrics = metrics.flatMap((item) => [item, `${item}_1`]);
          fieldReport.metrics = fieldReport.metrics.flatMap((item) => [item, `${item}_1`]);
     }
     fieldReport.metrics.forEach((item) => {
          const index = metrics.findIndex((el) => el === item);
          indexFilter.indexMetric.push(index);
     });
     fieldReport.properties.forEach((item) => {
          const index = properties.findIndex((el) => el === item);
          indexFilter.indexProperties.push(index);
     });

     let csvString = '';
     const stringLimit = 32000;
     const handleConvertDataExport = (returnRows) => {
          let { headerSegment, nameHeader, annotationRow, newHeader, compareDayRow } = getHeaderReport({
               headerColumn,
               segments,
               filterFields,
               isCompare,
               newDateRange,
          });
          let newHeaderColumn = newHeader.filter((item) => item.value !== 'viewDetail');
          if (returnRows?.length > 0 && segments?.length > 0) {
               segments.forEach((segment) => {
                    const newArraySegment = [];

                    returnRows.forEach((data) => {
                         for (const key in data) {
                              if (data.hasOwnProperty(key) && key.includes(camelCase(segment.id))) {
                                   newArraySegment.push({ [key]: data[key] });
                              }
                         }
                    });
               });
               if (newHeaderColumn.length < headerSegment.length) {
                    headerSegment = headerSegment.filter((item, index) => index < newHeaderColumn.length);
                    annotationRow = annotationRow.filter((item, index) => index < newHeaderColumn.length);
                    nameHeader = nameHeader.filter((item, index) => index < newHeaderColumn.length);
                    compareDayRow = compareDayRow.filter((item, index) => index < newHeaderColumn.length);
               }
          }
          const newData = [];
          if (segments?.length > 0) {
               newData.push(annotationRow, headerSegment, nameHeader);
               if (isCompare) {
                    newData.splice(2, 0, compareDayRow);
               }
          } else {
               if (isCompare) {
                    newData.push(annotationRow, compareDayRow);
               }
               newData.push(nameHeader);
          }

          returnRows.forEach((element) => {
               const newItem = [];
               newHeaderColumn.forEach((head) => {
                    const exitedHeader = Object.keys(element).find((key) => head.value === key);
                    if (exitedHeader && element[exitedHeader]) {
                         newItem.push(element[exitedHeader].value);
                    } else if (!reportType) {
                         newItem.push('None');
                    } else {
                         newItem.push('');
                    }
               });

               newData.push(newItem);
          });

          return newData;
     };
     const newResponse = [];
     const newProperty = filterFields.properties.filter((item) => item !== 'viewDetail');
     const findViewProfile = filterFields.properties.some((item) => item === 'viewDetail');
     if (reportName === REPORT_NAMES.FORM_CATEGORY_REPORT) {
          dataReport = dataReport.filter(
               (row) =>
                    (row.metricCompoundValues?.[0] && row.metricCompoundValues[0].value !== 'undefine') ||
                    (row[propertyType] && findViewProfile
                         ? row[propertyType][1] && row[propertyType][1].value !== 'undefine'
                         : row[propertyType][0] && row[propertyType][0].value !== 'undefine')
          );
     }
     dataReport.forEach((row) => {
          if (
               !isCompare &&
               ((row?.metricCompoundValues && row.metricCompoundValues[0]?.value !== 'undefine') ||
                    indexFilter.indexMetric.length === 0)
          ) {
               const newRow = { ...row };
               newRow.metricCompoundValues = newRow.metricCompoundValues.filter((el, index) => indexFilter.indexMetric.includes(index));
               newRow[`${propertyType}`] = newRow[`${propertyType}`].filter((el, index) => indexFilter.indexProperties.includes(index));
               return newResponse.push(newRow);
          } else if (isCompare) {
               const newRow = { ...row };
               newRow.metricCompoundValues = newRow.metricCompoundValues.filter((el, index) => indexFilter.indexMetric.includes(index));
               newRow[`${propertyType}`] = newRow[`${propertyType}`].filter((el, index) => indexFilter.indexProperties.includes(index));
               return newResponse.push(newRow);
          }
     });

     let returnRows = [];
     const metricCompoundValues = metrics.filter((item) => fieldReport.metrics.includes(item));
     returnRows = newResponse.map((row) =>
          Object.entries(row).reduce((newRow, [compoundKey, compoundValue]) => {
               let fieldValues = [];
               let keyCompound;

               switch (compoundKey) {
                    case 'dimensionCompoundValues':
                         keyCompound = fieldReport.dimensions;
                         break;
                    case 'metricCompoundValues':
                         keyCompound = metricCompoundValues;
                         break;
                    case `${propertyType}`:
                         keyCompound = newProperty;
                         break;
                    default:
               }
               // remove profile
               if (
                    compoundKey === `${propertyType}` &&
                    findViewProfile &&
                    (compoundValue[0].value === 'View' ||
                         (compoundValue[0].value === '' && reportName === REPORT_NAMES.ECOMMERCE_PURCHASE_TRANSACTION))
               ) {
                    compoundValue.shift();
               }
               // Merge all compoundValues into 1 object

               fieldValues = compoundValue.reduce((prevVal, currentVal, i) => {
                    if (i <= keyCompound.length - 1) {
                         let newCurrentVal = currentVal;
                         if (
                              getMetrics({
                                   segments,
                                   metrics: isCompare
                                        ? ['sessionDuration', 'avgSessionDuration', 'avgVisibleTimePerPage', 'avgVisibleTimeOnPage', 'lifeTimeAvgSessionDuration'].flatMap(
                                             (item) => [item, `${item}_1`]
                                        )
                                        : ['sessionDuration', 'avgSessionDuration', 'avgVisibleTimePerPage', 'avgVisibleTimeOnPage', 'lifeTimeAvgSessionDuration'],
                              }).includes(keyCompound[i]) &&
                              typeof currentVal.value === 'number'
                         ) {
                              newCurrentVal = { value: convertReportDuration(currentVal.value) };
                         }
                         if (isRateField({ value: keyCompound[i] })) {
                              if (reportType === 'Sales Conversion') {
                                   newCurrentVal = { value: currentVal.value === null || currentVal.value === '' ? '' : divideBy100(currentVal.value) };
                              } else {
                                   newCurrentVal = { value: divideBy100(currentVal.value) };
                              }
                         }
                         if (
                              ['userSourceFirst',
                                   'userSourceLast',
                                   'userSource',
                                   'userSourceFirstPersonCreated',
                                   'userSourceLastPersonCreated',
                                   'lifeTimeSourceFirst',
                                   'lifeTimeSourceLast',
                                   'closedSourceFirst',
                                   'closedSourceLast',
                                   'createdSourceFirst',
                                   'createdSourceLast',
                                   'closedChannelFirst',
                                   'closedChannelLast',
                                   'createdChannelFirst',
                                   'createdChannelLast',
                                   'lifeTimeChannelFirst',
                                   'lifeTimeChannelLast',
                                   'email',
                                   'currentCompany',
                                   'userSourceFirstND',
                                   'userSourceLastND',
                                   'userSourcePathND'
                              ].includes(keyCompound[i])
                              &&
                              reportName !== REPORT_NAMES.CONVERSION_SOURCE_ATTRIBUTION_REPORT
                         ) {
                              if (currentVal.value) {
                                   if (typeof currentVal.value.name !== 'undefined') {
                                        newCurrentVal = { value: currentVal.value.name };
                                   } else if (typeof currentVal.value.email !== 'undefined') {
                                        newCurrentVal = { value: currentVal.value.email };
                                   } else if (typeof currentVal.value.companyName !== 'undefined') {
                                        newCurrentVal = { value: currentVal.value.companyName };
                                   } else {
                                        newCurrentVal = { value: currentVal.value };
                                   }
                              } else {
                                   newCurrentVal = { value: 'None' };
                              }
                         }

                         if (([
                              REPORT_NAMES.SALES_CONVERSION_BY_CONVERSION, 
                              REPORT_NAMES.SALES_CONVERSION_BY_PROPERTY,
                              REPORT_NAMES.BY_CONVERSION,
                              REPORT_NAMES.BY_PROPERTY
                         ].includes(reportName) 
                              && ['userSourcePath', 'userSourcePathND'].includes(keyCompound[i])) || ((
                              ['userSourcePath',
                                   'noneDirectSourcePath',
                                   'userSourcePathPersonCreated',
                                   'createdSourcePath',
                                   'createdSourcePathND',
                                   'lifeTimeSourcePath',
                                   'lifeTimeSourcePathND',
                                   'closedSourcePath',
                                   'closedSourcePathND',
                                   'closedChannelPath',
                                   'closedChannelPathND',
                                   'createdChannelPath',
                                   'createdChannelPathND',
                                   'lifeTimeChannelPath',
                                   'lifeTimeChannelPathND',
                                   'dataSources',
                                   'enrichType',
                                   'emailList',
                                   'relatedCompanies',
                                   'volunteering', 
                                   'certifications', 
                                   'education', 
                                   'projects', 
                                   'recommendations', 
                                   'otherRoles',
                                   'userSourcePathND'
                              ].includes(keyCompound[i])
                              || (intentProperties.length > 0 && intentProperties.some((pro) => generateCustomDimensionName(`${pro?.name}_intent`) === keyCompound[i] && [INTENT_PROPERTIES_STORE.MULTIPLE, INTENT_PROPERTIES_STORE.SINGLE].includes(pro?.fieldType)))
                         ) &&
                              compoundKey === `${propertyType}`
                         )) {
                              const listSourceName = [];
                              // Helper function to format field based on type
                              const formatFieldValue = (source, fieldKey) => {
                                   // otherRoles: Company Name (Role, Start Date - End Date)
                                   if (fieldKey === 'otherRoles') {
                                        const companyName = source?.companyName || '';
                                        const role = source?.title || '';
                                        const startDate = source?.startDate || '';
                                        const endDate = source?.endDate || '';
                                        return `${companyName} (${role}, ${startDate} - ${endDate})`;
                                   }
                                   // volunteering: Company Name (Role, Start Date - End Date)
                                   if (fieldKey === 'volunteering') {
                                        const companyName = source?.companyName || '';
                                        const role = source?.role || '';
                                        const startDate = source?.startDate || '';
                                        const endDate = source?.endDate || '';
                                        return `${companyName} (${role}, ${startDate} - ${endDate})`;
                                   }
                                   // education: School Name (Degree, Start Year - End Year)
                                   if (fieldKey === 'education') {
                                        const schoolName = source?.schoolName || '';
                                        const degree = source?.degree || '';
                                        const startYear = source?.startYear || '';
                                        const endYear = source?.endYear || '';
                                        return `${schoolName} (${degree}, ${startYear} - ${endYear})`;
                                   }
                                   // certifications: Cert Name (Company Name, Year)
                                   if (fieldKey === 'certifications') {
                                        const title = source?.title || '';
                                        const companyName = source?.companyName || '';
                                        const year = source?.year || '';
                                        return `${title} (${companyName}, ${year})`;
                                   }
                                   // recommendations: Recommender Name - Summary
                                   if (fieldKey === 'recommendations') {
                                        const recommenderName = source?.recommenderName || '';
                                        const recommendation = source?.recommendation || '';
                                        return `${recommenderName} - ${recommendation}`;
                                   }
                                   // projects: Project Title (URL)
                                   if (fieldKey === 'projects') {
                                        const title = source?.title || '';
                                        const url = source?.url || '';
                                        return `${title} (${url})`;
                                   }
                                   // Default: use existing logic
                                   if (source?.email) return source?.email;
                                   if (source?.companyName) return source?.companyName;
                                   if (source?.title) return source?.title;
                                   if (source?.schoolName) return source?.schoolName;
                                   if (source?.recommenderName) return source?.recommenderName;
                                   if (fieldKey === 'enrichType' && source?.value) return source?.value;
                                   return source?.name;
                              };

                              if (Array.isArray(currentVal.value)) {
                                   currentVal.value.map((source) => {
                                        const formattedValue = formatFieldValue(source, keyCompound[i]);
                                        if (formattedValue) {
                                             listSourceName.push(formattedValue);
                                        }
                                        return formattedValue;
                                   });
                              } else if (currentVal.value?.name) {
                                   listSourceName.push(currentVal.value?.name);
                              } else if (currentVal.value?.email) {
                                   listSourceName.push(currentVal.value?.email);
                              } else if (currentVal.value?.companyName) {
                                   listSourceName.push(currentVal.value?.companyName);
                              } else if (currentVal.value?.value) {
                                   listSourceName.push(currentVal.value?.value);
                              }
                              // Use ' | ' separator for new formatted fields, comma for others
                              const separator = ['otherRoles', 'volunteering', 'education', 'certifications', 'recommendations', 'projects'].includes(keyCompound[i]) ? ' | ' : ',';
                              newCurrentVal = { value: listSourceName.length > 0 ? listSourceName.join(separator) : 'None' };
                         }

                         if (keyCompound[i] === 'employees' && currentVal.value !== 'none') {
                              newCurrentVal = { value: currentVal.value + ' employees' };
                         }

                         if (keyCompound[i] === 'funnelOrder') {
                              newCurrentVal = { value: currentVal.value || 0 };
                         }

                         if (keyCompound[i] === 'pSegments') {
                              const segmentsMatchedRecordIndex = [];
                              segments.forEach((segmentSelected, segmentSelectedIndex) => {
                                   if (currentVal.value?.includes(segmentSelected.id)) {
                                        segmentsMatchedRecordIndex.push(`S${segmentSelectedIndex + 1}`);
                                   }
                              });
                              newCurrentVal = { value: segmentsMatchedRecordIndex };
                         }

                         if (newCurrentVal.value?.length > 32000) {
                              let newkeyCompound = {};
                              const lastIndex = Math.ceil(newCurrentVal.value.length / stringLimit);
                              let indexof;
                              const head = headerColumn.find((item, index) => {
                                   indexof = index;
                                   return item.value === `${keyCompound[i]}`;
                              });
                              for (let j = 0; j < lastIndex; j++) {
                                   if (head && j !== lastIndex - 1) {
                                        const newColumn = { ...head, label: `${head.label}_${j}`, value: `${head.value}_${j}` };
                                        if (!headerColumn.find((head) => head.value === newColumn.value)) {
                                             headerColumn.splice(indexof + (j + 1), 0, newColumn);
                                        }
                                   }
                                   let newCurrentValSplit = {
                                        value: `${newCurrentVal.value.toString().substr(j * stringLimit, stringLimit)}`,
                                   };
                                   newkeyCompound = {
                                        ...newkeyCompound,
                                        [`${keyCompound[i]}${j === 0 ? `` : `_${j - 1}`}`]: newCurrentValSplit,
                                   };
                              }
                              return { ...prevVal, ...newkeyCompound };
                         }
                         return { ...prevVal, [keyCompound[i]]: newCurrentVal };
                    } else {
                         return { ...prevVal };
                    }
               }, {});
               if (compoundKey === 'metricCompoundValues') {
                    let newFieldValues = {};
                    metricCompoundValues.forEach((item) => {
                         newFieldValues = { ...newFieldValues, [item]: fieldValues[item] };
                    });
                    fieldValues = { ...newFieldValues };
               }
               return { ...newRow, ...fieldValues };
          }, {})
     );
     // }
     // Split rows by \n for list custom properties (video events)
     const listCustomPropertyFields = customProperties
          .filter((cp) => VIDEO_EVENTS_GROUP_BY_NAME.includes(cp.eventObjectName))
          .map((cp) => cp.properties);

     if (listCustomPropertyFields.length > 0) {
          const expandedRows = [];
          returnRows.forEach((row) => {
               // Find max split count across all list properties in this row
               let maxParts = 1;
               listCustomPropertyFields.forEach((field) => {
                    if (row[field] && typeof row[field].value === 'string' && row[field].value.includes('\n')) {
                         const parts = row[field].value.split('\n').length;
                         if (parts > maxParts) maxParts = parts;
                    }
               });

               if (maxParts === 1) {
                    expandedRows.push(row);
               } else {
                    for (let i = 0; i < maxParts; i++) {
                         const newRow = { ...row };
                         listCustomPropertyFields.forEach((field) => {
                              if (newRow[field] && typeof newRow[field].value === 'string' && newRow[field].value.includes('\n')) {
                                   const parts = newRow[field].value.split('\n');
                                   newRow[field] = { value: parts[i] !== undefined ? parts[i] : '' };
                              }
                         });
                         expandedRows.push(newRow);
                    }
               }
          });
          returnRows = expandedRows;
     }

     const valueFormatCSV = handleConvertDataExport(returnRows);
     csvString = convertJsonToCSV(valueFormatCSV);
     return csvString;
};
export const exportVisibleCustomReport = ({
     data,
     filterFields,
     headerColumn,
     reportName,
     reportCompare,
     isCompare,
     reportType,
     isCustom = false,
     report,
     properties,
     customProperties = [],
}) => {
     const dataReport = isCompare ? [...reportCompare.rows] : [...data.rows];
     const newDimensions = [];
     const newProperties = [];
     const newMetrics = [];
     report.dimensions.forEach((item) => {
          if (filterFields.dimensions.find((el) => el === camelCase(item.name))) {
               newDimensions.push(camelCase(item.name));
          }
     });
     report.properties.forEach((item) => {
          if (filterFields.properties.find((el) => el === item.value)) {
               newProperties.push(item.value);
          }
     });
     properties.forEach((item) => {
          if (filterFields.properties.find((el) => el === item.key) && !newProperties.find((el) => el === item.key)) {
               newProperties.push(item.key);
          }
     });

     report.metrics.forEach((item) => {
          if (filterFields.metrics.find((el) => el === camelCase(item.name))) {
               newMetrics.push(camelCase(item.name));
          }
     });
     const indexFilter = {
          indexMetric: [],
          indexProperties: [],
     };
     filterFields.metrics.forEach((item) => {
          const index = newMetrics.findIndex((el) => el === item);
          indexFilter.indexMetric.push(index);
     });
     filterFields.properties.forEach((item) => {
          const index = newProperties.findIndex((el) => el === item);
          indexFilter.indexProperties.push(index);
     });
     let csvString = '';
     const stringLimit = 32000;
     let headerCustom = [];
     if (filterFields.dimensions?.length > 0) {
          filterFields.dimensions.forEach((d) => headerCustom.push(d));
     }
     if (filterFields.properties?.length > 0) {
          filterFields.properties.forEach((p) => headerCustom.push(p));
     }
     if (filterFields.metrics?.length > 0) {
          filterFields.metrics.forEach((m) => headerCustom.push(m));
     }
     const handleConvertDataExport = (returnRows) => {
          const newHeader = [];
          headerColumn.forEach((element) => {
               newHeader.push(element.label);
          });
          const newData = [];
          newData.push(newHeader);
          returnRows.forEach((element) => {
               const newItem = [];
               headerCustom.forEach((head) => {
                    const exitedHeader = Object.keys(element).find((key) => head === key);
                    if (exitedHeader) {
                         if (typeof element[exitedHeader].value === 'undefined') {
                              newItem.push('');
                         } else {
                              newItem.push(element[exitedHeader].value);
                         }
                    } else {
                         newItem.push('None');
                    }
               });
               newData.push(newItem);
          });
          return newData;
     };

     let returnRows = dataReport.map((row) =>
          Object.entries(row).reduce((newRow, [compoundKey, compoundValue]) => {
               let fieldValues = [];
               let keyCompound;

               switch (compoundKey) {
                    case 'dimensionCompoundValues':
                         keyCompound = newDimensions;
                         break;
                    case 'metricCompoundValues':
                         keyCompound = newMetrics;
                         break;
                    case 'propertiesCompoundValues':
                         keyCompound = newProperties;
                         break;
                    default:
               }
               // Merge all compoundValues into 1 object
               fieldValues = compoundValue.reduce((prevVal, currentVal, i) => {
                    if (i <= keyCompound.length - 1) {
                         let personId = {};
                         let newCurrentVal = currentVal;

                         if (
                              keyCompound[i] === 'userSourceFirst' ||
                              keyCompound[i] === 'userSourceLast' ||
                              keyCompound[i] === 'userSource' ||
                              keyCompound[i] === 'userSourceFirstPersonCreated' ||
                              keyCompound[i] === 'userSourceLastPersonCreated'
                         ) {
                              if (currentVal.value) {
                                   if (typeof currentVal.value === 'string') {
                                        newCurrentVal = { value: currentVal.value };
                                   } else {
                                        newCurrentVal = { value: currentVal.value.name };
                                   }
                              } else {
                                   newCurrentVal = { value: 'none' };
                              }
                         }
                         if (
                              keyCompound[i] === 'userSourcePath' ||
                              keyCompound[i] === 'noneDirectSourcePath' ||
                              keyCompound[i] === 'userSourcePathPersonCreated'
                         ) {
                              const listSourceName = [];
                              if (typeof currentVal.value === 'string') {
                                   listSourceName.push(currentVal.value);
                              } else {
                                   currentVal.value.map((source) => {
                                        listSourceName.push(source.name);
                                        return source.name;
                                   });
                              }
                              newCurrentVal = { value: listSourceName.toString() };
                         }

                         if (keyCompound[i] === 'employees' && currentVal.value !== 'none') {
                              newCurrentVal = { value: currentVal.value + ' employees' };
                         }
                         if (newCurrentVal.value?.length > 32000) {
                              let newkeyCompound = {};
                              const lastIndex = Math.ceil(newCurrentVal.value.length / stringLimit);
                              let indexof;
                              const head = headerCustom.find((item, index) => {
                                   indexof = index;
                                   return item === `${keyCompound[i]}`;
                              });
                              for (let j = 0; j < lastIndex; j++) {
                                   if (head && j !== lastIndex - 1) {
                                        const newColumn = {
                                             label: headerColumn[indexof].label + '_' + j,
                                             value: headerColumn[indexof].value + '_' + j,
                                        };
                                        const newColCus = head + '_' + j;
                                        if (!headerCustom.find((head) => head === newColCus)) {
                                             headerColumn.splice(indexof + (j + 1), 0, newColumn);
                                             headerCustom.splice(indexof + (j + 1), 0, newColCus);
                                        }
                                   }
                                   let newCurrentValSplit = { value: `${newCurrentVal.value.toString().substr(j * stringLimit, stringLimit)}` };
                                   newkeyCompound = { ...newkeyCompound, [`${keyCompound[i]}${j === 0 ? `` : `_${j - 1}`}`]: newCurrentValSplit };
                              }
                              return { ...prevVal, ...newkeyCompound, ...personId };
                         }
                         return { ...prevVal, [keyCompound[i]]: newCurrentVal, ...personId };
                    } else {
                         return {};
                    }
               }, {});
               return { ...newRow, ...fieldValues };
          }, {})
     );
     // Split rows by \n for list custom properties (video events)
     const listCustomPropertyFields = customProperties
          .filter((cp) => VIDEO_EVENTS_GROUP_BY_NAME.includes(cp.eventObjectName))
          .map((cp) => cp.properties);

     if (listCustomPropertyFields.length > 0) {
          const expandedRows = [];
          returnRows.forEach((row) => {
               let maxParts = 1;
               listCustomPropertyFields.forEach((field) => {
                    if (row[field] && typeof row[field].value === 'string' && row[field].value.includes('\n')) {
                         const parts = row[field].value.split('\n').length;
                         if (parts > maxParts) maxParts = parts;
                    }
               });

               if (maxParts === 1) {
                    expandedRows.push(row);
               } else {
                    for (let i = 0; i < maxParts; i++) {
                         const newRow = { ...row };
                         listCustomPropertyFields.forEach((field) => {
                              if (newRow[field] && typeof newRow[field].value === 'string' && newRow[field].value.includes('\n')) {
                                   const parts = newRow[field].value.split('\n');
                                   newRow[field] = { value: parts[i] !== undefined ? parts[i] : '' };
                              }
                         });
                         expandedRows.push(newRow);
                    }
               }
          });
          returnRows = expandedRows;
     }

     const valueFormatCSV = handleConvertDataExport(returnRows);
     csvString = convertJsonToCSV(valueFormatCSV);
     return csvString;
};

export const getSegmentField = (segmentId, field) => {
     return `${camelCase(segmentId)}_${field}`;
};

export const getOriginalField = (field, segments = []) => {
     const newMetric = field.split('_').length > 1 && segments.length > 0 ? field.replace(field.split('_')[0] + '_', '') : field;
     return newMetric;
};

export const idSegment = (id) => {
     return typeof id === 'string' && id.length === 32 && /^[0-9A-Fa-f]+$/.test(id);
}

export const getURLImg = (alias, isCustom) => {
     if (isCustom) return '/assets/icons/listenlayer-favicon.svg';

     switch (alias) {
          case 'microsoft-ads':
               return `/assets/icons/ms-advertising-ads/icon-color.svg`;
          case 'linkedin-ads':
               return `/assets/icons/linkedIn-ads/icon-color.svg`;
          default:
               return `/assets/icons/${alias}/icon-color.svg`;
     }
};

export const escapeRegExp = (string) => {
     return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // Escape special characters
}

export const extractDomain = (input) => {
     const url = input.includes("://") ? input : `http://${input}`;
     const domain = new URL(url).hostname;
     return domain.startsWith("www.") ? domain.slice(4) : domain;
}

export const handleAddMetricSalesConversion = (secondaryObject, reportName) => {
     const totalActivity = secondaryObject.map((secondaryObject) => {
          const name = secondaryObject.name;
          const key = !secondaryObject.isSalesForce ? sanitizeTableName(`${secondaryObject.name}${secondaryObject.externalId}`) : secondaryObject.name;
          return {
               key: `total${key}ObjectCreated`,
               label: `${name} Objects Created`,
               isDefault: false,
               section: SECTION_REPORT.TOTAL_ACTIVITY,
               object: name,
               description: `The total number of ${name} created in the date range. This object is included in the report because it is a Primary or Secondary Object on a Sales Conversion, allowing it to be used in calculations.`
          };
     })
     if (reportName === REPORT_NAMES.SALES_CONVERSION_BY_PROPERTY) return totalActivity;

     const conversionRate = secondaryObject.map((secondaryObject) => {
          const name = secondaryObject.name;
          const key = !secondaryObject.isSalesForce ? sanitizeTableName(`${secondaryObject.name}${secondaryObject.externalId}`) : secondaryObject.name;
          return {
               key: `conversionEvent${key}ObjectRate`,
               label: `${name} Conv. Rate (All Conversions)`,
               isDefault: false,
               section: SECTION_REPORT.CONV_RATE,
               object: name,
               description: `The conversion rate for All Conversions based on ${name}s created in the date range. This metric is calculated only for Conversions that have ${secondaryObject.name}s included as a Primary or Secondary Object. All Conversions / ${secondaryObject.name}s Created.`
          };
     })

     const uniqueConversionRate = secondaryObject.map((secondaryObject) => {
          const name = secondaryObject.name;
          const key = !secondaryObject.isSalesForce ? sanitizeTableName(`${secondaryObject.name}${secondaryObject.externalId}`) : secondaryObject.name;
          return {
               key: `uniqueConversion${key}ObjectRate`,
               label: `${name} Conv. Rate (Unique Conversions)`,
               isDefault: false,
               section: SECTION_REPORT.CONV_RATE,
               object: name,
               description: `The conversion rate for All Conversions based on ${name}s created in the date range. This metric is calculated only for Conversions that have ${secondaryObject.name}s included as a Primary or Secondary Object. Unique Conversions / ${secondaryObject.name}s Created.`
          };
     })

     return [...totalActivity, ...conversionRate, ...uniqueConversionRate];
}

export const handleAddMetricSalesConvNameIndividual = (salesConversionName, reportName) => {
     if (reportName === REPORT_NAMES.SALES_CONVERSION_BY_CONVERSION) return [];

     const countConvName = [];
     const valueConvName = [];
     const convRateConvName = [];

     for (const conv of salesConversionName) {
          const { conversionName, conversionID } = conv;
          countConvName.push({
               key: `individual${conversionID}Count`,
               label: `Conversions (${conversionName})`,
               isDefault: false,
               section: SECTION_REPORT.COUNTS,
               sectionParent: `Individual Conv. (${conversionName})`,
               conversionID,
               description: `All Conversions (${conversionName}) events.`
          })
          countConvName.push({
               key: `individual${conversionID}CountUnique`,
               label: `Unique Conversions (${conversionName})`,
               isDefault: false,
               section: SECTION_REPORT.COUNTS,
               sectionParent: `Individual Conv. (${conversionName})`,
               conversionID,
               description: `Counts one conversion event (${conversionName}) per Primary Object. The Primary Object is the main object that generates the conversion based on the sales conversion settings.`
          })
          valueConvName.push({
               key: `individual${conversionID}Value`,
               label: `Conversions (${conversionName}) Value`,
               isDefault: false,
               section: SECTION_REPORT.VALUES,
               sectionParent: `Individual Conv. (${conversionName})`,
               conversionID,
               description: `The SUM of the currency-based value assigned to All Conversion (${conversionName}) events in the report.`
          })
          valueConvName.push({
               key: `individual${conversionID}ValueUnique`,
               label: `Unique Conversions (${conversionName}) Value (Primary Object)`,
               isDefault: false,
               section: SECTION_REPORT.VALUES,
               sectionParent: `Individual Conv. (${conversionName})`,
               conversionID,
               description: `The SUM of the currency-based value assigned to Unique Conversion (${conversionName}) events in the report. Unique Conversions are based on one conversion in the date range per Primary Object record. We use the last value when multiple Values exist.`
          })
          valueConvName.push({
               key: `individual${conversionID}AvgConvValue`,
               label: `Avg. Conv. Value (${conversionName}s)`,
               isDefault: false,
               section: SECTION_REPORT.VALUES,
               sectionParent: `Individual Conv. (${conversionName})`,
               conversionID,
               description: `Conversions (${conversionName}) Value / Conversions (${conversionName}).`
          })
          valueConvName.push({
               key: `individual${conversionID}AvgConvValueUnique`,
               label: `Avg. Conv. Value (Unique ${conversionName}s)`,
               isDefault: false,
               section: SECTION_REPORT.VALUES,
               sectionParent: `Individual Conv. (${conversionName})`,
               conversionID,
               description: `Unique Conversions (${conversionName}) Value / Unique Conversions (${conversionName}).`
          })
          convRateConvName.push({
               key: `individual${conversionID}SessionRate`,
               label: `Session Conv. Rate (Conversions (${conversionName}))`,
               isDefault: false,
               section: SECTION_REPORT.CONV_RATE,
               sectionParent: `Individual Conv. (${conversionName})`,
               conversionID,
               description: `The total conversion rate for Sessions (${conversionName}). Conversions (${conversionName}) / Total Sessions.`
          })
          convRateConvName.push({
               key: `individual${conversionID}SessionRateUnique`,
               label: `Session Conv. Rate (Unique Conversions (${conversionName}))`,
               isDefault: false,
               section: SECTION_REPORT.CONV_RATE,
               sectionParent: `Individual Conv. (${conversionName})`,
               conversionID,
               description: `The total conversion rate for Sessions (Unique Conversions (${conversionName})). Unique Conversions (${conversionName}) / Total Sessions.`
          })
          convRateConvName.push({
               key: `individual${conversionID}UserRate`,
               label: `User Conv. Rate (Conversions (${conversionName}))`,
               isDefault: false,
               section: SECTION_REPORT.CONV_RATE,
               sectionParent: `Individual Conv. (${conversionName})`,
               conversionID,
               description: `The total conversion rate for Active Users (${conversionName}). Conversions (${conversionName}) / Total Active Users.`
          })
          convRateConvName.push({
               key: `individual${conversionID}UserRateUnique`,
               label: `User Conv. Rate (Unique Conversions (${conversionName}))`,
               isDefault: false,
               section: SECTION_REPORT.CONV_RATE,
               sectionParent: `Individual Conv. (${conversionName})`,
               conversionID,
               description: `The total conversion rate for Active Users (Unique Conversions (${conversionName})). Unique Conversions (${conversionName}) / Total Active Users.`
          })
          convRateConvName.push({
               key: `individual${conversionID}PrimaryObjectRate`,
               label: `Primary Object Conv. Rate (Conversions (${conversionName}))`,
               isDefault: false,
               section: SECTION_REPORT.CONV_RATE,
               sectionParent: `Individual Conv. (${conversionName})`,
               conversionID,
               description: `The conversion rate for Conversions (${conversionName}) based on the number of Primary Objects created in the date range. Conversions (${conversionName}) / Primary Objects Created.`
          })
          convRateConvName.push({
               key: `individual${conversionID}PrimaryObjectRateUnique`,
               label: `Primary Object Conv. Rate (Unique Conversions (${conversionName}))`,
               isDefault: false,
               section: SECTION_REPORT.CONV_RATE,
               sectionParent: `Individual Conv. (${conversionName})`,
               conversionID,
               description: `The conversion rate for Unique Conversions (${conversionName}) based on the number of Primary Objects created in the date range. Unique Conversions (${conversionName}) / Primary Objects Created.`
          })
     }

     return [...countConvName, ...valueConvName, ...convRateConvName];
}

export const getReportTypeByName = (reportName) => {
     const entry = Object.entries(REPORT_TYPE_TO_NAMES_MAP)
          .find(([, names]) => names.includes(reportName));

     return entry?.[0] ?? null;
};

export const filterOutContractPlans = (items) => {
     if (!items || !Array.isArray(items)) return [];

     const enableNames = [
          'test contract_identity',
          'contract test identity1',
          'Tuyet testing contract plan',
          'Chuyen Contact plan final',
          'Thu Ha company couting',
          'thuhatest-identify',
          'contract test identity',
          'test-contract4',
          'Contract_SF',
     ];
     const disableNames = ['test', 'dev', 'thuha', 'phuong', 'tuyet', 'chuyen'];

     return items.filter((item) => enableNames.includes(item.internalName) || !disableNames.some((name) => item.clientEmail?.toLowerCase().includes(name)));
};

export const getStatusTag = (value, scoped, originalField) => {
     if (!value || !Array.isArray(value)) {
          return []
     }
     let newData = value.map((v) => {
          let dataTag
          switch (originalField) {
               case 'enrichType':
                    dataTag = STATUS_TAG_FIELD_REPORT[v.value]
                    return {
                         ...v,
                         description: dataTag?.description.replace('[type_enrich]', v.name),
                         color: dataTag?.color
                    }
               case 'dataSources':
                    dataTag = STATUS_TAG_FIELD_REPORT[`${scoped}-${v.name}`]
                    return {
                         ...v,
                         description: dataTag?.description,
                    }
               default:
                    return v
          }
     })
     return newData
}
export const getFriendlyNameTooltip = ({ field }) => {
     if (!field) return '';
     switch (field) {
          case 'currentB2BRole':
               return 'Current B2B Role'

          default:
               break;
     }
     return field
          // add space
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          // split and upcase fisrt letters
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
}

export const formatTimestamps = (value, type, timeZone) => {
     let dateValue;
     if (timeZone) {
          dateValue = dayjs(value).utc().tz(timeZone);
     } else {
          dateValue = dayjs(value).utc();
     }
     switch (type) {
          case 'isoDate':
               return dateValue.format('YYYY-MM-DD');
          case 'usDate':
               return dateValue.format('MM/DD/YYYY');
          case 'europeanDate':
               return dateValue.format('DD/MM/YYYY');
          case 'fullMonthTime':
               return dateValue.format('MMMM D, YYYY HH:mm:ss z');
          case 'shortMonthTime':
               return dateValue.format('MMM D YYYY HH:mm:ss');
          case 'rfc2822':
               return dateValue.format('ddd, DD MMM YYYY HH:mm:ss ZZ');
          case 'unixEpochSeconds':
               return String(Math.floor(dateValue.unix()));
          case 'jsEpochMilliseconds':
               return String(dateValue.valueOf());

          case 'iso8601UTC':
               return dateValue.utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
          case 'iso8601Offset':
               return dateValue.format('YYYY-MM-DDTHH:mm:ssZ');
          case 'iso8601Milliseconds':
               return dateValue.format('YYYY-MM-DDTHH:mm:ss.SSSZ');

          default:
               return dateValue.toISOString();
     }
}

export function tryParseDate(val) {
     if (!val) return null;
     for (const fmt of DATE_FORMATS) {
          const d = parse(val, fmt, new Date());
          if (isValid(d)) return d;
     }
     return null;
}

export function normalizeExample(val) {
     if (val === null || val === undefined) return '';

     const str = String(val).trim();
     if (str === '') return '';

     const parsed = tryParseDate(str);
     if (parsed) {
          const hasTime = /[T\s]\d{1,2}:/.test(str);
          return hasTime
               ? format(parsed, 'yyyy-MM-dd HH:mm:ss')
               : format(parsed, 'yyyy-MM-dd');
     }

     return str;
}

export const getSectionCustomPropertiesDefault = (property, scoped) => {
     const { pathPropertyName } = property;
     let section = SECTION_REPORT.CUSTOM_PROPERTIES;
     let sectionParent = '';
     if (pathPropertyName.includes('consent') || pathPropertyName.includes('trackingLevel') || pathPropertyName.includes('identifyStrength')) {
          section = SECTION_REPORT.CONSENT;
     }
     if (pathPropertyName.includes('geography')) {
          section = SECTION_REPORT.GEOGRAPHY;
     }
     if (pathPropertyName.includes('browse')) {
          section = SECTION_REPORT.BROWSER;
     }
     if (pathPropertyName.includes('device')) {
          section = SECTION_REPORT.DEVICE;
     }
     if (pathPropertyName.includes('sourceAutomaticValues')) {
          if(scoped === SCOPED.SESSION){
               if(pathPropertyName.includes('utm')) {
                    sectionParent = SECTION_REPORT.SOURCE;
                    section = SECTION_REPORT.UTM;
               } else {
                    section = SECTION_REPORT.SOURCE;
               }
          } else if(pathPropertyName.includes('utm')) {
               sectionParent = SECTION_REPORT.LIFETIME_SOURCE_SUMMARY;
               section = SECTION_REPORT.UTM;
          } else {
               section = SECTION_REPORT.LIFETIME_SOURCE_SUMMARY;
          }
     }
     if (pathPropertyName.includes('pageAutomaticValues') || pathPropertyName.includes('eventLocation')) {
          section = SECTION_REPORT.PAGE_ACTIVITY;
     }
     if (pathPropertyName.includes('conversion')) {
          section = SECTION_REPORT.WEB_CONVERSIONS;
     }
     if (pathPropertyName.includes('form')) {
          section = SECTION_REPORT.FORM_ACTIVITY;
     }
     if (pathPropertyName.includes('triggers')) {
          section = SECTION_REPORT.TRIGGERS;
     }
     if (['userDetails.userID', 'id', 'companyId', 'userDetails.sessionID', 'personId'].includes(pathPropertyName)) {
          section = SECTION_REPORT.FORM_ACTIVITY;
     }
     return { section, sectionParent };
}

export const extractName = (value) => {
     if (value == null) return '';                 
     if (typeof value === 'string') return value;  
     if (Array.isArray(value)) {
          return value.map(v => v?.name || '').filter(v => v).join(',') || 'None';
     }
     if (typeof value === 'object') return value.name ?? ''; 
     return String(value);                        
}

export const md5 = (inputString) => {
     let hc = '0123456789abcdef';

     function rh(n) {
          let j,
               s = '';

          for (j = 0; j <= 3; j++) s += hc.charAt((n >> (j * 8 + 4)) & 0x0f) + hc.charAt((n >> (j * 8)) & 0x0f);

          return s;
     }

     function ad(x, y) {
          let l = (x & 0xffff) + (y & 0xffff);
          let m = (x >> 16) + (y >> 16) + (l >> 16);

          return (m << 16) | (l & 0xffff);
     }

     function rl(n, c) {
          return (n << c) | (n >>> (32 - c));
     }

     function cm(q, a, b, x, s, t) {
          return ad(rl(ad(ad(a, q), ad(x, t)), s), b);
     }

     function ff(a, b, c, d, x, s, t) {
          return cm((b & c) | (~b & d), a, b, x, s, t);
     }

     function gg(a, b, c, d, x, s, t) {
          return cm((b & d) | (c & ~d), a, b, x, s, t);
     }

     function hh(a, b, c, d, x, s, t) {
          return cm(b ^ c ^ d, a, b, x, s, t);
     }

     function ii(a, b, c, d, x, s, t) {
          return cm(c ^ (b | ~d), a, b, x, s, t);
     }

     function sb(x) {
          let i;
          let nblk = ((x.length + 8) >> 6) + 1;
          let blks = new Array(nblk * 16);

          for (i = 0; i < nblk * 16; i++) blks[i] = 0;
          for (i = 0; i < x.length; i++) blks[i >> 2] |= x.charCodeAt(i) << ((i % 4) * 8);
          blks[i >> 2] |= 0x80 << ((i % 4) * 8);
          blks[nblk * 16 - 2] = x.length * 8;

          return blks;
     }

     let i,
          x = sb('' + inputString),
          a = 1732584193,
          b = -271733879,
          c = -1732584194,
          d = 271733878,
          olda,
          oldb,
          oldc,
          oldd;

     for (i = 0; i < x.length; i += 16) {
          olda = a;
          oldb = b;
          oldc = c;
          oldd = d;
          a = ff(a, b, c, d, x[i + 0], 7, -680876936);
          d = ff(d, a, b, c, x[i + 1], 12, -389564586);
          c = ff(c, d, a, b, x[i + 2], 17, 606105819);
          b = ff(b, c, d, a, x[i + 3], 22, -1044525330);
          a = ff(a, b, c, d, x[i + 4], 7, -176418897);
          d = ff(d, a, b, c, x[i + 5], 12, 1200080426);
          c = ff(c, d, a, b, x[i + 6], 17, -1473231341);
          b = ff(b, c, d, a, x[i + 7], 22, -45705983);
          a = ff(a, b, c, d, x[i + 8], 7, 1770035416);
          d = ff(d, a, b, c, x[i + 9], 12, -1958414417);
          c = ff(c, d, a, b, x[i + 10], 17, -42063);
          b = ff(b, c, d, a, x[i + 11], 22, -1990404162);
          a = ff(a, b, c, d, x[i + 12], 7, 1804603682);
          d = ff(d, a, b, c, x[i + 13], 12, -40341101);
          c = ff(c, d, a, b, x[i + 14], 17, -1502002290);
          b = ff(b, c, d, a, x[i + 15], 22, 1236535329);
          a = gg(a, b, c, d, x[i + 1], 5, -165796510);
          d = gg(d, a, b, c, x[i + 6], 9, -1069501632);
          c = gg(c, d, a, b, x[i + 11], 14, 643717713);
          b = gg(b, c, d, a, x[i + 0], 20, -373897302);
          a = gg(a, b, c, d, x[i + 5], 5, -701558691);
          d = gg(d, a, b, c, x[i + 10], 9, 38016083);
          c = gg(c, d, a, b, x[i + 15], 14, -660478335);
          b = gg(b, c, d, a, x[i + 4], 20, -405537848);
          a = gg(a, b, c, d, x[i + 9], 5, 568446438);
          d = gg(d, a, b, c, x[i + 14], 9, -1019803690);
          c = gg(c, d, a, b, x[i + 3], 14, -187363961);
          b = gg(b, c, d, a, x[i + 8], 20, 1163531501);
          a = gg(a, b, c, d, x[i + 13], 5, -1444681467);
          d = gg(d, a, b, c, x[i + 2], 9, -51403784);
          c = gg(c, d, a, b, x[i + 7], 14, 1735328473);
          b = gg(b, c, d, a, x[i + 12], 20, -1926607734);
          a = hh(a, b, c, d, x[i + 5], 4, -378558);
          d = hh(d, a, b, c, x[i + 8], 11, -2022574463);
          c = hh(c, d, a, b, x[i + 11], 16, 1839030562);
          b = hh(b, c, d, a, x[i + 14], 23, -35309556);
          a = hh(a, b, c, d, x[i + 1], 4, -1530992060);
          d = hh(d, a, b, c, x[i + 4], 11, 1272893353);
          c = hh(c, d, a, b, x[i + 7], 16, -155497632);
          b = hh(b, c, d, a, x[i + 10], 23, -1094730640);
          a = hh(a, b, c, d, x[i + 13], 4, 681279174);
          d = hh(d, a, b, c, x[i + 0], 11, -358537222);
          c = hh(c, d, a, b, x[i + 3], 16, -722521979);
          b = hh(b, c, d, a, x[i + 6], 23, 76029189);
          a = hh(a, b, c, d, x[i + 9], 4, -640364487);
          d = hh(d, a, b, c, x[i + 12], 11, -421815835);
          c = hh(c, d, a, b, x[i + 15], 16, 530742520);
          b = hh(b, c, d, a, x[i + 2], 23, -995338651);
          a = ii(a, b, c, d, x[i + 0], 6, -198630844);
          d = ii(d, a, b, c, x[i + 7], 10, 1126891415);
          c = ii(c, d, a, b, x[i + 14], 15, -1416354905);
          b = ii(b, c, d, a, x[i + 5], 21, -57434055);
          a = ii(a, b, c, d, x[i + 12], 6, 1700485571);
          d = ii(d, a, b, c, x[i + 3], 10, -1894986606);
          c = ii(c, d, a, b, x[i + 10], 15, -1051523);
          b = ii(b, c, d, a, x[i + 1], 21, -2054922799);
          a = ii(a, b, c, d, x[i + 8], 6, 1873313359);
          d = ii(d, a, b, c, x[i + 15], 10, -30611744);
          c = ii(c, d, a, b, x[i + 6], 15, -1560198380);
          b = ii(b, c, d, a, x[i + 13], 21, 1309151649);
          a = ii(a, b, c, d, x[i + 4], 6, -145523070);
          d = ii(d, a, b, c, x[i + 11], 10, -1120210379);
          c = ii(c, d, a, b, x[i + 2], 15, 718787259);
          b = ii(b, c, d, a, x[i + 9], 21, -343485551);
          a = ad(a, olda);
          b = ad(b, oldb);
          c = ad(c, oldc);
          d = ad(d, oldd);
     }
     return rh(a) + rh(b) + rh(c) + rh(d);
};

export const formatTableName = ({ id, name, prefix = 'Raw', isObject = false }) => {
     const safePrefix = sanitizeTableName(prefix);
     const safeName = sanitizeTableName(name);
     if(isObject){
          return `${safePrefix}${safeName}`;
     }
     return `ExtData${safePrefix}${safeName}`;
};

export const getExternalRelationTypeByObject = (sourceObj = '', targetObj = '', objects = []) => {
     const prioritySource = objects.find((item) => formatTableName({
          name: item.object,
          prefix: '',
          isObject: true,
     }) === sourceObj);
     const priorityTarget = objects.find((item) => formatTableName({
          name: item.object,
          prefix: '',
          isObject: true,
     }) === targetObj);

     if (!prioritySource || !priorityTarget) {
          return { relationType: null, isSourceFirst: null };
     }

     let isSourceFirst = true;
     let relationType;

     if (prioritySource.priority < priorityTarget.priority) {
          relationType = `${sourceObj}-${targetObj}`;
     } else {
          relationType = `${targetObj}-${sourceObj}`;
          isSourceFirst = false;
     }

     return { relationType, isSourceFirst };
};
export const capitalizeFirstLetterAll = (str) => {
  str = str.toLowerCase(); 
  let words = str.split(' '); 
  
  let capitalizedWords = words.map(word => {
    if (word.length === 0) return ''; 
    return word.charAt(0).toUpperCase() + word.slice(1);
  });
  
  return capitalizedWords.join(' ');
};
